import * as React from 'react';
import { FC, useState } from 'react';
import { useRefresh, useDataProvider, useNotify, Confirm } from 'react-admin';
import Button from '@mui/material/Button';
import DesktopAccessDisabledIcon from '@mui/icons-material/DesktopAccessDisabled';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

interface Props {
    resource_guid: string;
    // When discontinuing, the order (tilausnumero) the discontinuation targets.
    tilausnumero?: string;
    // 'discontinue' (default) ends the license, 'restore' reactivates a discontinued one.
    mode?: 'discontinue' | 'restore';
    disabled?: boolean;
    // Optional text shown next to the icon (e.g. the order number).
    buttonLabel?: string;
    // Button size, forwarded to MUI Button.
    size?: 'small' | 'medium' | 'large';
}

const CustomDBDiscontinueLicenseButton: FC<Props> = ({
    disabled = false,
    mode = 'discontinue',
    tilausnumero,
    buttonLabel,
    resource_guid,
    size = 'medium',
}) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const refresh = useRefresh();
    const notify = useNotify();
    const dataProvider = useDataProvider() as any;

    const isRestore = mode === 'restore';

    const handleClick = e => {
        e.stopPropagation();
        setOpen(true);
    };

    const handleDialogClose = () => {
        setOpen(false);
    };

    const handleConfirm = async () => {
        setLoading(true);
        try {
            if (isRestore) {
                await dataProvider.restoreUserLicense(resource_guid);
            } else {
                await dataProvider.discontinueUserLicense(
                    resource_guid,
                    tilausnumero
                );
            }
            refresh();
        } catch (error) {
            notify(
                `Virhe ${isRestore ? 'palautuksessa' : 'keskeytyksessä'}: ${error}`,
                { type: 'warning' }
            );
        } finally {
            setLoading(false);
            setOpen(false);
        }
    };

    return (
        <>
            <Button
                onClick={disabled ? undefined : handleClick}
                disabled={loading || disabled}
                color={isRestore ? 'warning' : 'success'}
                size={size}
            >
                {isRestore ? (
                    <RestartAltIcon />
                ) : disabled ? (
                    <DesktopAccessDisabledIcon />
                ) : (
                    <DesktopWindowsOutlinedIcon />
                )}
                {buttonLabel ? (
                    <span style={{ marginLeft: 4 }}>{buttonLabel}</span>
                ) : null}
            </Button>
            <Confirm
                isOpen={open}
                loading={loading}
                title={
                    isRestore
                        ? 'Palauta lisenssi aktiiviseksi'
                        : 'Keskeytä valittu lisenssi'
                }
                content={
                    isRestore
                        ? 'Haluatko varmasti palauttaa keskeytetyn lisenssin takaisin aktiiviseksi?'
                        : tilausnumero
                          ? `Haluatko varmasti keskeyttää lisenssin tilaukseen ${tilausnumero}?`
                          : 'Haluatko varmasti keskeyttää valitsemasi lisenssin pysyvästi?'
                }
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
            />
        </>
    );
};

export default CustomDBDiscontinueLicenseButton;
